// The % in front of every Gantt row's name (owner, 2026-10-12).
//
// Leaves carry their own number; a parent's is the duration-weighted average of its tasks (2026-10-13),
// computed on the server (back/utils/ganttProgress) and never typed. A leaf
// is editable inline for whoever may edit that row's dates (owner,
// 2026-10-12): admin standing or owner-side ganttEdit on every leaf (the
// feed's access.can.ganttProgressEdit), a partner with allowPlanEdit on the
// leaves their own company is responsible for (can.ganttProgressEditOwnRows,
// the same rows the chart lets them drag); everyone else reads it.
//
// Click the % -> a number box; Enter or leaving the box saves, Escape cancels.
import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";

// Parent ids from the WHOLE item list (a filtered or partner-scoped view
// still knows a row is a parent from its children's parentId).
export function parentIdsOf(items) {
  const ids = new Set((items || []).map((i) => String(i._id)));
  const out = new Set();
  for (const it of items || []) {
    const pid = it && it.parentId ? String(it.parentId) : "";
    if (pid && pid !== String(it._id) && ids.has(pid)) out.add(pid);
  }
  return out;
}

// CoordinationGantt's canEditProgress from the server's capabilities: true
// (every leaf), a predicate (a partner's own company's rows - the same test
// the pages use for dragging dates), or false. The server applies the same
// rule (back/utils/ganttProgress.canEditRowProgress) and refuses the rest.
export function progressEditRule(can, myCompany) {
  if (can && can.ganttProgressEdit) return true;
  if (can && can.ganttProgressEditOwnRows) {
    const mine = String(myCompany || "");
    return (item) => !!mine && !!item?.responsibleCompanyId && String(item.responsibleCompanyId) === mine;
  }
  return false;
}

// Whole percent for display; stored values keep one decimal.
export function pctText(v) {
  const n = Number(v);
  return `${Number.isFinite(n) ? Math.round(Math.max(0, Math.min(100, n))) : 0}%`;
}

export const PARENT_TIP = "Average of its tasks, weighted by duration";

// FIELD PROGRESS NOBODY ENTERED (owner, 2026-10-13: "never 0% and never
// done"). The server marks it (back/utils/ganttProgress.decorateRows):
// progressUnknown -> a dash with "no data entered"; a parent with some rows
// entered shows the average of those and says how many have no data.
export const UNKNOWN_TIP = "No data entered";
// A SHOP DRAWING ROW PRICED BY ITS SHEETS (back/utils/sheetProgress): the
// server stores why on progressBy.name - "from 3 of 5 sheets released; held
// at 95% until sign-off" - and that is the tip, on a parent or a leaf.
export function sheetsTip(item) {
  const by = item && item.progressBy;
  if (!by || by.source !== "sheets") return "";
  const t = String(by.name || "from released sheets");
  return t.charAt(0).toUpperCase() + t.slice(1);
}
export function pctDisplay(item, isParent) {
  const st = sheetsTip(item);
  if (st) return { text: pctText(item && item.progress), tip: st, sheets: true };
  if (item && item.progressUnknown) return { text: "\u2014", tip: UNKNOWN_TIP, unknown: true };
  const m = item && item.progressMissing;
  if (isParent && m && m.unknown && item.progressKnown !== undefined && item.progressKnown !== null) {
    return { text: `${pctText(item.progressKnown)}*`, tip: `${PARENT_TIP}, of the rows with data - ${m.unknown} of ${m.leaves} rows have no data entered` };
  }
  return { text: pctText(item && item.progress), tip: isParent ? PARENT_TIP : "" };
}

const BOX = { flex: "0 0 auto", minWidth: 34, fontVariantNumeric: "tabular-nums", fontSize: 11.5, textAlign: "right" };

export default function ProgressCell({ item, isParent, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const doneRef = useRef(false);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);
  const stop = (e) => e.stopPropagation();
  const shown = pctDisplay(item, isParent);
  const text = shown.text;

  if (isParent) {
    return (
      <Tooltip title={shown.tip}>
        <span data-testid={`gantt-pct-${item._id}`} data-parent="1" onClick={stop} onMouseDown={stop}
          style={{ ...BOX, color: "#555", fontWeight: 600, cursor: "help" }}>{text}</span>
      </Tooltip>
    );
  }
  if (!canEdit) {
    const plain = <span data-testid={`gantt-pct-${item._id}`} style={{ ...BOX, color: "#666" }}>{text}</span>;
    return shown.unknown || shown.sheets ? <Tooltip title={shown.tip}>{plain}</Tooltip> : plain;
  }
  const finish = async (save) => {
    if (doneRef.current) return;
    doneRef.current = true;
    const n = Number(draft);
    // On a row with no data entered, typing its stored default (0) IS the entry.
    const same = !shown.unknown && Math.abs(n - (Number(item.progress) || 0)) < 0.05;
    if (!save || draft === "" || !Number.isFinite(n) || n < 0 || n > 100 || same) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try { await onSave(Math.round(n * 10) / 10); } finally { setSaving(false); setEditing(false); }
  };
  if (editing) {
    return (
      <span onClick={stop} onMouseDown={stop} style={{ ...BOX, display: "inline-flex", alignItems: "center" }}>
        <input ref={inputRef} type="number" min={0} max={100} step={1} value={draft} disabled={saving}
          aria-label={`Progress of ${item.name}`} data-testid={`gantt-pct-input-${item._id}`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") finish(true); else if (e.key === "Escape") finish(false); }}
          onBlur={() => finish(true)}
          style={{ width: 46, fontSize: 11.5, padding: "0 2px", height: 18, textDecoration: "none" }} />
        %
      </span>
    );
  }
  return (
    <Tooltip title={shown.unknown ? `${UNKNOWN_TIP} - click to set progress` : shown.sheets ? `${shown.tip} - click to set it by hand instead` : "Click to set progress"}>
      <span role="button" tabIndex={0} data-testid={`gantt-pct-${item._id}`}
        onMouseDown={stop}
        onClick={(e) => { e.stopPropagation(); doneRef.current = false; setDraft(String(Math.round((Number(item.progress) || 0) * 10) / 10)); setEditing(true); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); doneRef.current = false; setDraft(String(Number(item.progress) || 0)); setEditing(true); } }}
        style={{ ...BOX, color: "#1677ff", cursor: "pointer", borderBottom: "1px dashed #91caff" }}>{text}</span>
    </Tooltip>
  );
}
